import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { DataTypeModelKey } from "app/models/dataType.model";
import { DeviceAggregatedData, DeviceDataModel, DeviceDataType } from "app/models/deviceData.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataService } from "app/services/api/data.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import * as moment from "moment";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Subscription } from "rxjs";
import { locale as catalan } from "./../i18n/ca";
import { locale as english } from "./../i18n/en";
import { locale as spanish } from "./../i18n/es";
import { EventModelSeverity } from "app/models/event.model";
import { AlarmModelTimeWindow } from "app/models/alarm.model";
import { DeviceMqttService } from "app/services/api/device.mqtt.service";
import * as Highcharts from "highcharts/highcharts";
import { chartSoundPressure5MinutesCard } from "app/chart-config/chart-sound-pressure-5-minutes-card";
import { IMqttMessage } from "ngx-mqtt";
import { ContextService } from "app/services/context.service";

@Component({
    selector: "device-card-soundpressure",
    templateUrl: "./device-card-soundpressure.component.html",
    styleUrls: ["./device-card-soundpressure.component.scss"],
    standalone: false,
})
export class DeviceCardSoundPressure implements OnInit, OnChanges, OnDestroy {
    @Input() device: ProjectDeviceModel = {
        connected: true,
        lastData: null,
        dataAggregation: { soundPressureLevel: { average: 0 } },
    } as unknown as ProjectDeviceModel;
    @Input() showDeviceLink = true;
    @Input() showAvg5 = true;
    @Input() showAvg30 = true;
    @Input() editGraphViews = false;
    @Input() dataType: DataTypeModelKey = DataTypeModelKey.soundPressureLevel;

    readonly device$ = new BehaviorSubject<ProjectDeviceModel | null>(null);

    loadingData = false;
    deviceData: DeviceDataModel[];
    sensorGraphData: any = null;

    statusValue: EventModelSeverity;
    statusAvg5: EventModelSeverity;
    statusAvg30: EventModelSeverity;

    subsInstant: Subscription;
    subsStatus: Subscription;
    subsAvg5: Subscription;
    subsAvg30: Subscription;
    subsBucket: Subscription;

    Highcharts = Highcharts; // required
    chart = JSON.parse(JSON.stringify(chartSoundPressure5MinutesCard));
    chartRef: Highcharts.Chart | null = null;

    get unitLabel(): string {
        if (
            this.dataType === DataTypeModelKey.soundPressureLevelC ||
            this.dataType === DataTypeModelKey.soundPressureLevelC1Minute ||
            this.dataType === DataTypeModelKey.rolling5sC ||
            this.dataType === DataTypeModelKey.rolling5C ||
            this.dataType === DataTypeModelKey.rolling30C
        ) {
            return "dBC";
        }
        if (this.dataType === DataTypeModelKey.soundPressureLevelCMinusA) {
            return "dB";
        }
        return "dBA";
    }

    get rolling30Value(): number | null {
        const rollingType = this.rolling30DataType;
        if (!rollingType) {
            return null;
        }
        const value = this.device?.lastData?.[rollingType]?.value;
        return typeof value === "number" ? value : null;
    }

    private get rolling30DataType(): DataTypeModelKey | null {
        if (this.dataType === DataTypeModelKey.soundPressureLevel) {
            return DataTypeModelKey.rolling30;
        }
        if (this.dataType === DataTypeModelKey.soundPressureLevelC) {
            return DataTypeModelKey.rolling30C;
        }
        return null;
    }

    constructor(
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private readonly dataService: DataService,
        private readonly projectDeviceService: ProjectDeviceService,
        private readonly deviceMqtt: DeviceMqttService,
        private toastr: ToastrService,
        public contextService: ContextService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan);
    }

    ngOnInit() {
        if (!this.device?.id) {
            return;
        }
        this.fetchData();
        this.subscribeToTopics();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["device"]) {
            this.device$.next(this.device);
        }
    }

    ngOnDestroy(): void {
        this.subsInstant?.unsubscribe();
        this.subsStatus?.unsubscribe();
        this.subsAvg5?.unsubscribe();
        this.subsAvg30?.unsubscribe();
        this.subsBucket?.unsubscribe();
    }

    chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
        this.applyChartData();
    };

    private get isRollingCard(): boolean {
        return (
            this.dataType === DataTypeModelKey.rolling5s ||
            this.dataType === DataTypeModelKey.rolling5sC ||
            this.dataType === DataTypeModelKey.rolling5 ||
            this.dataType === DataTypeModelKey.rolling5C ||
            this.dataType === DataTypeModelKey.rolling30 ||
            this.dataType === DataTypeModelKey.rolling30C
        );
    }

    private subscribeToTopics() {
        this.subsInstant = this.deviceMqtt
            .deviceTopic(this.device.id, DeviceDataType.instant, this.dataType)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());
                this.setLastDataValue(item.value, item.timestamp);
                this.cdr.detectChanges();
            });

        if (this.showAvg30 && this.rolling30DataType) {
            this.subsAvg30 = this.deviceMqtt
                .deviceTopic(this.device.id, DeviceDataType.instant, this.rolling30DataType)
                .subscribe((data: IMqttMessage) => {
                    const item = JSON.parse(data.payload.toString());
                    this.setLastDataValue(item.value, item.timestamp, this.rolling30DataType);
                    this.cdr.detectChanges();
                });
        }

        this.subsStatus = this.deviceMqtt.deviceTopic(this.device.id, DeviceDataType.status, null).subscribe((data: IMqttMessage) => {
            const item = JSON.parse(data.payload.toString());
            this.device.connected = item.connected;
            this.device.activeEvents = item.activeEvents;
            this.refreshAlarms();
            this.cdr.detectChanges();
        });

        this.subsAvg5 = this.deviceMqtt
            .deviceTopic(this.device.id, DeviceDataType.fiveMinutes, this.dataType)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());
                this.setAggregationValue(item.average);
                this.cdr.detectChanges();
            });

        this.subsBucket = this.deviceMqtt
            .deviceTopic(this.device.id, DeviceDataType.bucket, this.dataType)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());

                const last = this.deviceData[this.deviceData.length - 1];

                if (moment(last.date).format("hh:mm") == moment(item.date).format("hh:mm")) {
                    this.deviceData[this.deviceData.length - 1].aggregation = item.aggregation;
                } else {
                    this.deviceData.shift();
                    this.deviceData.push(item);
                }
                this.createSensorData();
                this.cdr.detectChanges();
            });
    }

    private refreshData() {
        if (this.loadingData) {
            return;
        }
        this.loadingData = true;

        this.fetchDevice();
        this.fetchData();
    }

    private refreshAlarms() {
        this.statusAvg5 = this.device.getAlarmStatus(this.dataType, AlarmModelTimeWindow.AlarmModelTimeWindowBucket5);
        this.statusAvg30 = this.rolling30DataType
            ? this.device.getAlarmStatus(this.rolling30DataType)
            : EventModelSeverity.EventModelSeveritySuccess;
        this.statusValue = this.device.getAlarmStatus(this.dataType, AlarmModelTimeWindow.AlarmModelTimeWindowInstant);
    }

    private fetchDevice() {
        // Fetch bucket of data of last hour (12 buckets)
        this.projectDeviceService.get(this.device.id).subscribe(
            (data: ProjectDeviceModel) => {
                this.device = Object.assign(new ProjectDeviceModel(), data);
                this.device$.next(this.device);
                this.refreshAlarms();
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    private fetchData() {
        // Fetch bucket of data of last hour (12 buckets)
        this.dataService.getDeviceDataSample(this.device.id, [this.dataType], 12, true).subscribe(
            (data: DeviceAggregatedData) => {
                this.deviceData = data[this.dataType];
                // From API data are coming from the most recent to the latest, reverting the order.
                this.deviceData.sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());
                this.createSensorData();
                this.loadingData = false;
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.loadingData = false;
            },
        );
    }

    private createSensorData() {
        if (!this.deviceData) {
            return;
        }

        const seriesData = [];
        const useBucketMax = this.isRollingCard;

        this.deviceData.forEach((data) => {
            const aggregationValue = useBucketMax ? data.aggregation.max : data.aggregation.average;
            const value = Math.round(aggregationValue * 10) / 10;
            const date = moment(data.date).format("HH:mm");
            seriesData.push([date, value]);
        });

        this.updateChartTooltip();
        this.chart.options.series[0].data = seriesData;
        this.applyChartData();
        this.cdr.detectChanges();
    }

    private updateChartTooltip() {
        const seriesName = this.isRollingCard
            ? this._fuseTranslationLoaderService.translateService.instant(`FIELD.${this.dataType}`) +
              ` ${this._fuseTranslationLoaderService.translateService.instant("COMPONENT.TooltipMax")}`
            : this._fuseTranslationLoaderService.translateService.instant(`FIELD.${this.dataType}`);

        this.chart.options.tooltip = {
            ...this.chart.options.tooltip,
            pointFormat: `<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.1f} ${this.unitLabel}</b><br/>`,
        };
        this.chart.options.series[0].name = seriesName;

        if (this.chartRef?.series?.[0]) {
            this.chartRef.update(
                {
                    tooltip: this.chart.options.tooltip as Highcharts.TooltipOptions,
                },
                false,
            );
            (this.chartRef.series[0] as Highcharts.Series).update({ name: seriesName } as Highcharts.SeriesOptionsType, false);
        }
    }

    private applyChartData() {
        if (this.chartRef?.series?.[0]) {
            this.chartRef.series[0].setData(this.chart.options.series[0].data as any[], false);
            this.chartRef.redraw();
            requestAnimationFrame(() => this.chartRef?.reflow());
            return;
        }

        this.chart.update = true;
        this.chart.visible = true;
    }

    private setLastDataValue(value: number, timestamp: Date, dataType = this.dataType) {
        if (!this.device.lastData) {
            this.device.lastData = {} as any;
        }
        if (!this.device.lastData[dataType]) {
            this.device.lastData[dataType] = { value, timestamp };
            return;
        }
        this.device.lastData[dataType].value = value;
        this.device.lastData[dataType].timestamp = timestamp;
    }

    private setAggregationValue(value: number) {
        if (!this.device.dataAggregation) {
            this.device.dataAggregation = {};
        }
        if (!this.device.dataAggregation[this.dataType]) {
            this.device.dataAggregation[this.dataType] = { average: value } as any;
            return;
        }
        this.device.dataAggregation[this.dataType].average = value;
    }
}
